import { useState } from "react";
import { AlertColor, Box, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import QuickAlert from "../../components/form/quickAlert";
import BoxContent from "../../components/boxContent";
import { FormInputDate } from "../../components/form/FormInputDate";
import useSatisfactionSurveyReport from "../../hooks/satisfactionSurvey";

export default function SatisfactionSurvey() {
  const { t } = useTranslation();
  const [visibleAlert, setVisibleAlert] = useState<boolean>(false);
  const [severityAlert, setSeverityAlert] = useState<AlertColor>("success");
  const [messageQuickAlert, setMessageQuickAlert] = useState("");
  const [titleMessage, setTitleMessage] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const { sendReportGeneration, report } = useSatisfactionSurveyReport();

  const convertToDate = (value: string) =>
    dayjs(`${value?.replace(/\//g, "-")}`);

  const toBeLessThanOrEqualToday = {
    name: "toBeLessThanOrEqualToday",
    test: (value = "") => {
      if (!value) return true;

      const selectedDate = dayjs(value);
      const today = dayjs().endOf("day");

      return selectedDate.isSameOrBefore(today);
    },
    message: t("satisfactionSurvey.validation.greaterThanToday"),
    exclusive: true,
  };

  const toBeGreaterThanInit = (initial = "") => ({
    name: "toBeGreaterThanInit",
    test: (final = "") => {
      const finalDate = convertToDate(final);
      const initialDate = convertToDate(initial);
      return finalDate.isSameOrAfter(initialDate);
    },
    message: t("satisfactionSurvey.validation.finalGreaterThanInitial"),
    exclusive: true,
  });

  const satisfactionSurveyReportSchema = (t: any) =>
    yup.object().shape({
      initialDate: yup
        .string()
        .required(t("satisfactionSurvey.validation.initialRequired"))
        .test({ ...toBeLessThanOrEqualToday }),

      finalDate: yup
        .string()
        .required(t("satisfactionSurvey.validation.finalRequired"))
        .test({ ...toBeLessThanOrEqualToday })
        .when("initialDate", ([initial]) => {
          if (initial) {
            return yup.string().test(toBeGreaterThanInit(initial));
          }
          return yup
            .string()
            .required(t("satisfactionSurvey.validation.finalRequired"));
        }),
    });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(satisfactionSurveyReportSchema(t) as any),
    mode: "onChange",
  });

  const onSubmit = async (data: any) => {
    setButtonLoading(true);

    sendReportGeneration(
      data,
      (result: any) => {
        if (result.status === "completed" && result.downloadUrl) {
          try {
            const link = document.createElement("a");
            const linkReplaced = result.downloadUrl.replace("xlsx", "zip");
            link.target = "_blank";
            link.href = linkReplaced;
            link.download = "app-rating-report.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setSeverityAlert("success");
            setTitleMessage(t("satisfactionSurvey.alert.successTitle"));
            setMessageQuickAlert(t("satisfactionSurvey.alert.successMessage"));
            setVisibleAlert(true);
          } catch (error) {
            setSeverityAlert("error");
            setTitleMessage(t("satisfactionSurvey.alert.errorTitle"));
            setMessageQuickAlert(t("satisfactionSurvey.alert.errorMessage"));
            setVisibleAlert(true);
          }
        }

        if (result.status === "failed") {
          setSeverityAlert("error");
          setTitleMessage(t("satisfactionSurvey.alert.errorTitle"));
          setMessageQuickAlert(t("satisfactionSurvey.alert.errorMessage"));
          setVisibleAlert(true);
        }
      },
      setButtonLoading,
    );
  };

  return (
    <BoxContent title={t("satisfactionSurvey.title")}>
      <Box
        sx={{
          marginBottom: "24px",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 400 }}>
          {t("satisfactionSurvey.description")}
        </span>
      </Box>

      <Box sx={{ display: "flex", gap: 4 }}>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            width: "70%",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <FormInputDate
              name="initialDate"
              control={control}
              label={t("satisfactionSurvey.initialDateLabel")}
              maxDate={dayjs()}
              error={errors.initialDate}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <FormInputDate
              name="finalDate"
              control={control}
              label={t("satisfactionSurvey.finalDateLabel")}
              maxDate={dayjs()}
              error={errors.finalDate}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          marginBottom: "32px",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 400 }}>
          {t("satisfactionSurvey.requiredFields")}
        </span>
      </Box>
      <Box
        sx={{
          marginBottom: "32px",
        }}
      >
        <Button
          variant="contained"
          disabled={!isValid || buttonLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {buttonLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
            </Box>
          ) : (
            t("satisfactionSurvey.downloadButton")
          )}
        </Button>
      </Box>

      <Box
        sx={{
          marginBottom: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "var(--gray-300)",
          }}
        >
          {t("satisfactionSurvey.privacyTitle")}
        </span>
        <span
          style={{ fontSize: 12, fontWeight: 400, color: "var(--gray-300)" }}
        >
          {t("satisfactionSurvey.privacyDescription")}
        </span>
      </Box>
      <QuickAlert
        visibility={visibleAlert}
        description={messageQuickAlert}
        severity={severityAlert}
        title={titleMessage}
        hanhleClose={() => setVisibleAlert(false)}
      />
    </BoxContent>
  );
}
